import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ChevronIcon } from '../../assets/icons/chevron-right.svg';
import { ReactComponent as CollapseRailIcon } from '../../assets/icons/chevrons-left.svg';
import { ReactComponent as ExpandRailIcon } from '../../assets/icons/chevrons-right.svg';
import Wordmark from '../Brand/Wordmark';
import {
  FONTS,
  MOTION,
  NAV,
  RADII,
  REDUCED_MOTION,
  focusVisible,
} from '../../theme/tokens';
import {
  NavContext,
  NavGroup,
  NavItem,
  NAV_GROUPS,
  Paint,
  SvgIcon,
  activeItemId,
  resolveGroups,
} from './sideNavConfig';

/**
 * The GritCargo customer sidebar — "A1 · Multi-open · smart headers".
 *
 * A data-driven, sectioned rail generated from `NAV_GROUPS`. Each section folds
 * open/closed independently (multi-open); a collapsed header still tells you how
 * many pages it hides and whether your current page lives inside it (the green
 * "you are here" dot). The whole panel also collapses to an icon-only rail
 * (with tooltips) to reclaim horizontal space. The active page is derived from
 * the route (the single source of truth); fold + collapse state persist to
 * localStorage so they survive a refresh.
 */

const STORAGE_KEY = 'gc_nav_open_v1';

type OpenMap = Record<string, boolean>;

/** All sections open by default; merge any persisted overrides on top. */
const readOpenMap = (groups: NavGroup[], storageKey: string): OpenMap => {
  const defaults: OpenMap = Object.fromEntries(groups.map((g) => [g.id, true]));
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw) return { ...defaults, ...(JSON.parse(raw) as OpenMap) };
  } catch {
    /* localStorage unavailable (e.g. private mode) — fall back to defaults */
  }
  return defaults;
};

const persistOpenMap = (map: OpenMap, storageKey: string): void => {
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(map));
  } catch {
    /* ignore */
  }
};

// ── Glyph ───────────────────────────────────────────────────────────────────
// Recolours a house SVG on the navy ground. Most glyphs paint via `fill`
// (overriding any hardcoded path fill); Devices/GritClaw paint via stroke
// (currentColor). The `& svg path` selector beats SVG presentation attributes.
interface GlyphProps {
  Icon: SvgIcon;
  paint: Paint;
  size: number;
  color: string;
  /** Rotation (deg) — used for the section chevron. */
  rotate?: number;
}

/** SVG primitives the house icon set uses beyond `<path>`. */
const GLYPH_SHAPE_SELECTOR =
  '& svg path, & svg line, & svg rect, & svg circle, & svg ellipse, & svg polyline, & svg polygon';

const Glyph: React.FC<GlyphProps> = ({
  Icon,
  paint,
  size,
  color,
  rotate = 0,
}) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      'display': 'inline-flex',
      'alignItems': 'center',
      'justifyContent': 'center',
      'flexShrink': 0,
      'width': size,
      'height': size,
      color,
      'transform': rotate ? `rotate(${rotate}deg)` : 'none',
      'transition': `transform ${MOTION.durationMedium}ms ${MOTION.easeOut}, color ${MOTION.durationShort}ms ${MOTION.easeOut}`,
      [REDUCED_MOTION]: { transition: 'none' },
      '& svg': {
        width: size,
        height: size,
        display: 'block',
        ...(paint === 'stroke' ? { stroke: color, fill: 'none' } : {}),
      },
      [GLYPH_SHAPE_SELECTOR]:
        paint === 'stroke' ? { stroke: color, fill: 'none' } : { fill: color },
    }}
  >
    <Icon />
  </Box>
);

// ── Group header (the "smart header") ────────────────────────────────────────
interface GroupHeaderProps {
  group: NavGroup;
  open: boolean;
  hasActive: boolean;
  count: number;
  disabled: boolean;
  onToggle: () => void;
}

const GroupHeader: React.FC<GroupHeaderProps> = ({
  group,
  open,
  hasActive,
  count,
  disabled,
  onToggle,
}) => {
  const { t } = useTranslation();
  const betaLabel = t('Beta');
  const accessibleLabel = group.beta
    ? `${t(group.label)} — ${betaLabel}`
    : t(group.label);

  return (
    <ButtonBase
      onClick={onToggle}
      disabled={disabled}
      aria-expanded={open}
      aria-controls={`nav-section-${group.id}`}
      aria-label={accessibleLabel}
      sx={{
        'width': '100%',
        'justifyContent': 'space-between',
        'alignItems': 'center',
        'gap': '8px',
        'padding': '7px 10px',
        'borderRadius': `${NAV.itemRadius}px`,
        'transition': `background-color ${MOTION.durationShort}ms ${MOTION.easeOut}`,
        [REDUCED_MOTION]: { transition: 'none' },
        '&:hover': { backgroundColor: NAV.hoverBg },
        ...focusVisible,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          minWidth: 0,
          flex: 1,
        }}
      >
        <Glyph
          Icon={ChevronIcon}
          paint="fill"
          size={11}
          color={NAV.sectionLabel}
          rotate={open ? 90 : 0}
        />
        <Glyph
          Icon={group.Icon}
          paint={group.paint}
          size={15}
          color={hasActive ? NAV.marker : NAV.sectionGlyphRest}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            minWidth: 0,
            flex: 1,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: FONTS.mono,
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: group.beta ? '0.16em' : '0.25em',
              textTransform: 'uppercase',
              lineHeight: 1,
              color: hasActive ? NAV.sectionLabelActive : NAV.sectionLabel,
              transition: `color ${MOTION.durationShort}ms ${MOTION.easeOut}`,
              [REDUCED_MOTION]: { transition: 'none' },
              whiteSpace: 'nowrap',
              overflow: group.beta ? 'visible' : 'hidden',
              textOverflow: group.beta ? 'clip' : 'ellipsis',
              flexShrink: group.beta ? 0 : 1,
            }}
          >
            {t(group.label)}
          </Typography>
          {group.beta && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                padding: '2px 5px',
                border: `1px solid ${NAV.betaBorder}`,
                borderRadius: `${RADII.pill}px`,
                backgroundColor: NAV.betaBg,
                color: NAV.betaFg,
                fontFamily: FONTS.body,
                fontSize: '9px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                lineHeight: 1,
                textTransform: 'uppercase',
              }}
            >
              {betaLabel}
            </Box>
          )}
        </Box>
      </Box>

      {/* When collapsed: how many pages are hidden + the "you are here" dot. */}
      {!open && !group.beta && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '7px',
            flexShrink: 0,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontFamily: FONTS.mono,
              fontSize: '11px',
              fontWeight: 500,
              color: NAV.countFg,
              lineHeight: 1,
            }}
          >
            {count}
          </Typography>
          {hasActive && (
            <Box
              aria-hidden
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: NAV.marker,
                flexShrink: 0,
              }}
            />
          )}
        </Box>
      )}
    </ButtonBase>
  );
};

// ── Item row ─────────────────────────────────────────────────────────────────
interface ItemRowProps {
  item: NavItem;
  active: boolean;
  disabled: boolean;
  beta?: boolean;
  /** Icon-only mode (whole rail collapsed). */
  collapsed: boolean;
  onClick: () => void;
}

const ItemRow: React.FC<ItemRowProps> = ({
  item,
  active,
  disabled,
  beta = false,
  collapsed,
  onClick,
}) => {
  const { t } = useTranslation();
  const label = t(item.label);
  const accessibleLabel = beta ? `${label} — ${t('Beta')}` : label;
  const fg = active ? NAV.activeFg : NAV.itemLabelRest;

  const button = (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? accessibleLabel : undefined}
      sx={{
        'width': collapsed ? 44 : '100%',
        'justifyContent': collapsed ? 'center' : 'flex-start',
        'gap': collapsed ? 0 : '12px',
        'padding': collapsed ? '11px' : '9px 10px',
        'borderRadius': `${NAV.itemRadius}px`,
        'backgroundColor': active ? NAV.activeBg : 'transparent',
        'transition': `background-color ${MOTION.durationShort}ms ${MOTION.easeOut}, transform ${MOTION.durationShort}ms ${MOTION.easeOut}`,
        [REDUCED_MOTION]: { transition: 'none' },
        '&:hover': { backgroundColor: active ? NAV.activeBg : NAV.hoverBg },
        '&:active': { transform: 'scale(0.98)' },
        ...focusVisible,
      }}
    >
      <Glyph Icon={item.Icon} paint={item.paint} size={20} color={fg} />
      {!collapsed && (
        <>
          <Typography
            component="span"
            title={label}
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
              fontFamily: FONTS.body,
              fontSize: '14px',
              fontWeight: active ? 600 : 500,
              color: fg,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              transition: `color ${MOTION.durationShort}ms ${MOTION.easeOut}`,
              [REDUCED_MOTION]: { transition: 'none' },
            }}
          >
            {label}
          </Typography>
          {item.badge != null && item.badge > 0 && (
            <Box
              component="span"
              sx={{
                flexShrink: 0,
                minWidth: 18,
                height: 18,
                padding: '0 5px',
                borderRadius: '9px',
                backgroundColor: active ? NAV.activeBadgeBg : NAV.badgeBg,
                color: active ? NAV.activeFg : NAV.badgeFg,
                fontFamily: FONTS.body,
                fontSize: '11px',
                fontWeight: 600,
                lineHeight: '18px',
                textAlign: 'center',
              }}
            >
              {item.badge > 99 ? '99+' : item.badge}
            </Box>
          )}
        </>
      )}
    </ButtonBase>
  );

  if (!collapsed) return button;
  return (
    <Tooltip title={accessibleLabel} placement="right">
      {/* span wrapper keeps the tooltip working even when the button is disabled */}
      <Box component="span" sx={{ display: 'inline-flex' }}>
        {button}
      </Box>
    </Tooltip>
  );
};

// ── Whole-rail collapse toggle ───────────────────────────────────────────────
interface RailToggleProps {
  collapsed: boolean;
  onClick: () => void;
}

const RailToggle: React.FC<RailToggleProps> = ({ collapsed, onClick }) => {
  const { t } = useTranslation();
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={collapsed ? t('Expand navigation') : t('Collapse navigation')}
      aria-expanded={!collapsed}
      sx={{
        'width': 32,
        'height': 32,
        'flexShrink': 0,
        'borderRadius': `${NAV.itemRadius}px`,
        'transition': `background-color ${MOTION.durationShort}ms ${MOTION.easeOut}`,
        [REDUCED_MOTION]: { transition: 'none' },
        '&:hover': { backgroundColor: NAV.hoverBg },
        ...focusVisible,
      }}
    >
      <Glyph
        Icon={collapsed ? ExpandRailIcon : CollapseRailIcon}
        paint="fill"
        size={16}
        color={NAV.itemLabelRest}
      />
    </ButtonBase>
  );
};

// ── SideNav ──────────────────────────────────────────────────────────────────
export interface SideNavProps {
  context: NavContext;
  /** Role-specific navigation groups. Customer groups are the default. */
  groups?: NavGroup[];
  /** Persisted open/closed state key for this role's groups. */
  openStorageKey?: string;
  /** Accessible nav label; useful when role shells share one engine. */
  ariaLabel?: string;
  /** When true (e.g. terms not accepted), navigation is dimmed + inert. */
  disabled?: boolean;
  /** Narrow the rail on smaller viewports. */
  compact?: boolean;
  /** Whole-panel collapsed (icon-only) state. */
  collapsed?: boolean;
  /** Toggle the whole-panel collapse. */
  onToggleCollapse: () => void;
}

const SideNav: React.FC<SideNavProps> = ({
  context,
  groups: navGroups = NAV_GROUPS,
  openStorageKey = STORAGE_KEY,
  ariaLabel = 'Navigation',
  disabled = false,
  compact = false,
  collapsed = false,
  onToggleCollapse,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const groups = useMemo(
    () => resolveGroups(navGroups, context),
    [context, navGroups]
  );
  const [openMap, setOpenMap] = useState<OpenMap>(() =>
    readOpenMap(navGroups, openStorageKey)
  );

  const activeId = useMemo(
    () => activeItemId(groups, location.pathname),
    [groups, location.pathname]
  );
  const activeGroupId = useMemo(
    () =>
      groups.find((g) => g.items.some((it) => it.id === activeId))?.id ?? null,
    [groups, activeId]
  );

  const toggleGroup = (id: string): void =>
    setOpenMap((prev) => {
      const next = { ...prev, [id]: !(prev[id] ?? true) };
      persistOpenMap(next, openStorageKey);
      return next;
    });

  const go = (path: string): void => {
    if (!disabled) navigate(path);
  };

  const width = collapsed
    ? NAV.widthCollapsed
    : compact
    ? NAV.widthCompact
    : NAV.width;
  const transitionMs = MOTION.durationMedium;

  return (
    // The rail's collapse animates the wrapper's grid track rather than a
    // `width` transition on the nav itself — same slide, but the animated
    // property stays off the layout-property blacklist.
    <Box
      sx={{
        flexShrink: 0,
        height: '100%',
        display: 'grid',
        gridTemplateColumns: `${width}px`,
        transition: `grid-template-columns ${transitionMs}ms ${MOTION.easeOut}`,
        [REDUCED_MOTION]: { transition: 'none' },
      }}
    >
      <Box
        component="nav"
        aria-label={t(ariaLabel)}
        sx={{
          minWidth: 0,
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: NAV.bg,
          borderRight: `1px solid ${NAV.hairline}`,
          boxShadow: 'none',
        }}
      >
        {/* Pinned brand header + collapse toggle */}
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            height: '64px',
            padding: collapsed ? '0' : '0 12px 0 16px',
            borderBottom: `1px solid ${NAV.hairline}`,
          }}
        >
          {!collapsed && <Wordmark color="#FFFFFF" size={21} />}
          <RailToggle collapsed={collapsed} onClick={onToggleCollapse} />
        </Box>

        {/* Scrolling navigation area */}
        <Box
          sx={{
            'flex': 1,
            'minHeight': 0,
            'overflowY': 'auto',
            'overflowX': 'hidden',
            'padding': collapsed ? '10px 0 6px' : '10px 10px 6px',
            'opacity': disabled ? 0.5 : 1,
            'pointerEvents': disabled ? 'none' : 'auto',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255,255,255,0.16)',
              borderRadius: 3,
            },
          }}
        >
          {collapsed
            ? groups.map((group, gi) => (
                <React.Fragment key={group.id}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px',
                    }}
                  >
                    {group.items.map((item) => (
                      <ItemRow
                        key={item.id}
                        item={item}
                        active={item.id === activeId}
                        disabled={disabled}
                        beta={group.beta}
                        collapsed
                        onClick={() => go(item.path)}
                      />
                    ))}
                  </Box>
                  {gi < groups.length - 1 && (
                    <Box
                      aria-hidden
                      sx={{
                        height: '1px',
                        backgroundColor: NAV.hairline,
                        margin: '8px 14px',
                      }}
                    />
                  )}
                </React.Fragment>
              ))
            : groups.map((group) => {
                const open = openMap[group.id] ?? true;
                const hasActive = group.id === activeGroupId;
                return (
                  <Box key={group.id} sx={{ mb: '4px' }}>
                    <GroupHeader
                      group={group}
                      open={open}
                      hasActive={hasActive}
                      count={group.items.length}
                      disabled={disabled}
                      onToggle={() => toggleGroup(group.id)}
                    />
                    {/* Grid-row collapse (0fr ↔ 1fr) — same reveal as MUI
                      Collapse without a per-frame `height` layout animation.
                      `visibility` transitions discretely, so the section stays
                      visible until fully closed and untabbable once hidden. */}
                    <Box
                      id={`nav-section-${group.id}`}
                      sx={{
                        display: 'grid',
                        gridTemplateRows: open ? '1fr' : '0fr',
                        visibility: open ? 'visible' : 'hidden',
                        transition: `grid-template-rows ${transitionMs}ms ${MOTION.easeOut}, visibility ${transitionMs}ms`,
                        [REDUCED_MOTION]: { transition: 'none' },
                      }}
                    >
                      <Box sx={{ minHeight: 0, overflow: 'hidden' }}>
                        {/* Indented sub-items with a subtle guide hairline. */}
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            marginLeft: `${NAV.itemIndent}px`,
                            paddingLeft: '10px',
                            borderLeft: `1px solid ${NAV.itemGuide}`,
                            pt: '2px',
                          }}
                        >
                          {group.items.map((item) => (
                            <ItemRow
                              key={item.id}
                              item={item}
                              active={item.id === activeId}
                              disabled={disabled}
                              beta={group.beta}
                              collapsed={false}
                              onClick={() => go(item.path)}
                            />
                          ))}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                );
              })}
        </Box>
      </Box>
    </Box>
  );
};

export default SideNav;
